/**
 * /decoded/:token — public read-only share card for a decoded brief.
 * Branded viral surface: anyone opening a shared link sees the brief + a
 * "Decode your own" CTA (tagged src=shared-brief).
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import DecoderBrief from "../components/DecoderBrief";
import { Loader, Flame, Bot } from "lucide-react";

const NAVY = "#0F1B2D";
const ORANGE = "#FF6A00";
const YELLOW = "#FFC145";
const CREAM = "#F6F1E7";

export default function DecodedBrief() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/challenge/decoded/${token}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "This brief doesn't exist — or its link was mistyped.");
      });
  }, [token]);

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="decoded-brief-page">
      <div style={{ background: NAVY }} className="px-6 pt-10 pb-14">
        <div className="max-w-3xl mx-auto">
          <div className="font-mono-rm text-[10px] uppercase tracking-[3px] mb-5" style={{ color: YELLOW }}>
            · RESMO · Opportunity Decoder · Shared brief
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border-2 flex-shrink-0"
                  style={{ borderColor: ORANGE, color: ORANGE, background: "rgba(255,106,0,0.12)" }}>
              <Bot size={18} />
            </span>
            <h1 className="text-2xl sm:text-3xl font-black leading-tight text-white" data-testid="decoded-brief-headline">
              {data?.result?.role_title
                ? <>Decoded: <span style={{ color: ORANGE }}>{data.result.role_title}</span></>
                : <>A decoded <span style={{ color: ORANGE }}>job opportunity.</span></>}
            </h1>
          </div>
          <p className="mt-3 text-sm max-w-xl" style={{ color: "#94A3B8" }}>
            Someone ran this job description through RESMO's free Opportunity Decoder —
            here's what the employer is really asking for.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 -mt-6 pb-16">
        <div className="bg-white border-[3px] p-5 sm:p-7" style={{ borderColor: ORANGE, boxShadow: `6px 6px 0 0 ${NAVY}` }}>
          {error ? (
            <div className="text-center py-10" data-testid="decoded-brief-error">
              <p className="text-sm font-bold" style={{ color: "#B91C1C" }}>{error}</p>
              <Link to="/challenge?src=shared-brief"
                    className="mt-4 inline-flex items-center gap-2 px-6 py-3 border-2 font-black text-xs uppercase tracking-[1.5px]"
                    style={{ background: ORANGE, borderColor: NAVY, color: "white", boxShadow: `3px 3px 0 0 ${NAVY}` }}>
                Decode your own — free
              </Link>
            </div>
          ) : !data ? (
            <div className="flex justify-center py-14 font-mono-rm text-xs uppercase tracking-widest" style={{ color: "#64748B" }}>
              <Loader size={14} className="animate-spin mr-2" /> Loading the brief…
            </div>
          ) : (
            <DecoderBrief
              result={data.result}
              ctaTo="/challenge?src=shared-brief"
              ctaLabel="Decode your own job description — free"
              ctaTestId="decoded-brief-cta"
            />
          )}
        </div>

        <div className="text-center mt-10">
          <div className="inline-flex items-center gap-2 font-bold text-sm italic" style={{ color: ORANGE }}>
            <Flame size={16} /> Every career begins with a SPARK.
          </div>
          <div className="mt-3 font-mono-rm text-[10px] uppercase tracking-[2px]" style={{ color: "#94A3B8" }}>
            RESMO · Beyond the resume ·{" "}
            <Link to={`/early-access?src=shared-brief${data?.result?.role_title ? `&career=${encodeURIComponent(data.result.role_title)}` : ""}`}
                  className="underline" style={{ color: ORANGE }} data-testid="decoded-brief-early-access-link">
              Join early access
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
